import { ApolloProvider } from '@apollo/client'
import ErrorBoundary from 'lib/boundaries/ErrorBoundary'
import { PosthogManagerProvider } from 'lib/hooks/providers/PosthogManagerProvider'
import MaintenanceMode from 'lib/layouts/MaintenanceMode'
import Root from 'lib/layouts/RootLayout'
import 'utils/css/reset.css'
import 'material-symbols/outlined.css'
import SegmentScript from 'lib/utils/SegmentScript'
import Seo from 'lib/utils/Seo'
import Zendesk from 'lib/utils/Zendesk'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import Router from 'next/router'
import setPageName from 'utils/analytics'

import PartnerProvider from '@/hooks/providers/PartnerProvider'
import { PostHogProvider } from '@/hooks/providers/PosthogProvider'
import { authClient } from '@/utils/apollo'

const Toast = dynamic(() => import('lib/ui/Toast'), {
  ssr: false
})

declare global {
  interface Window {
    analytics: any
  }
}

// Track client-side page views with Segment
Router.events.on('routeChangeComplete', () => {
  window?.analytics?.page(setPageName({ route: Router.route, app: 'Auth' }))
})

function AuthApp({ Component, pageProps }: AppProps) {
  const segmentKey = process.env.NEXT_PUBLIC_SEGMENT_KEY
  const enableMaintenance = process.env.NEXT_PUBLIC_MAINTENANCE_MODE === 'true'

  return (
    <ApolloProvider client={authClient}>
      <PostHogProvider>
        <PosthogManagerProvider>
          <PartnerProvider>
            <Seo />
            <SegmentScript app='Auth' segmentKey={segmentKey} />
            <Toast />
            <Root>
              {enableMaintenance ? (
                <MaintenanceMode />
              ) : (
                <ErrorBoundary>
                  <Component {...pageProps} />
                </ErrorBoundary>
              )}
            </Root>
            <Zendesk />
          </PartnerProvider>
        </PosthogManagerProvider>
      </PostHogProvider>
    </ApolloProvider>
  )
}

export default AuthApp
